/**
 * Hilfe-Inhalte für alle Bereiche von Vertrieb, Vertriebsleiter und Vertriebsdirektor.
 * Überschreibt gleichnamige Einträge aus helpContent.js.
 * Zusatzfelder: audience („Wer sieht das?“) und roleNotes ({rolle: [Hinweise]}).
 */
const REP = "Vertrieb";
const LEAD = "Vertriebsleiter / Filialleiter";
const DIR = "Vertriebsdirektor";
const ADM = "Admin";
const ALL_SALES = `${REP} · ${LEAD} · ${DIR} · ${ADM}`;
const LEADS_UP = `${LEAD} · ${DIR} · ${ADM}`;
const DIR_UP = `${DIR} · ${ADM}`;

export const ROLE_LABELS = {
  sales_rep: REP,
  sales_manager: "Vertriebsleiter",
  branch_manager: "Filialleiter",
  sales_director: DIR,
  admin: ADM,
};

export const SALES_HELP = {
  // ------------------------------------------------------------------ Global
  "app.global": {
    title: "Novatus Sales Pro · Überblick",
    audience: "Alle angemeldeten Nutzer",
    summary:
      "Die App begleitet ein Wärmepumpen- oder PV-Projekt vom ersten Kontakt bis zur Schlussrechnung: " +
      "Kunde anlegen, Verbrauch und Heizlast erfassen, V-Check vor Ort, Angebot erstellen und unterschreiben lassen, " +
      "BzA/KfW-Prozess, Übergabe an Back-Office und Installateur. Menüs und Rechte passen sich automatisch deiner Rolle an.",
    workflow: [
      "Dashboard öffnen – dort siehst du deine Kennzahlen und die zuletzt bearbeiteten Projekte.",
      "„Neues Projekt“ anlegen und die Kundendaten erfassen.",
      "Im Projekt die Tabs von links nach rechts abarbeiten: Kunde → Verbrauch → Technik (V-Check) → Angebot.",
      "Angebot vor Ort oder per Link unterschreiben lassen.",
      "Im Tab „Status“ den weiteren Ablauf (BzA, KfW, Anzahlung, Installation) verfolgen.",
    ],
    tips: [
      "Das „?“ oben rechts zeigt immer die Hilfe zu genau dem Bereich, in dem du gerade bist.",
      "„KI fragen“ im Hilfe-Fenster stellt dem KI-Assistenten eine passende Frage zu diesem Bereich.",
      "Die App funktioniert auch offline – Eingaben und Fotos werden automatisch nachgeladen, sobald wieder Netz da ist.",
      "Über den Feedback-Button meldest du Fehler oder Wünsche direkt an das Novatus-Team.",
    ],
    roleNotes: {
      sales_rep: ["Du siehst deine eigenen Projekte und alle Bereiche, die du für Beratung, V-Check und Angebot brauchst."],
      sales_manager: ["Du siehst zusätzlich die Projekte deines Teams, das Kanban-Board, die Team-Auswertung und die Intake-Pipeline."],
      sales_director: ["Du siehst alle Projekte deines Bereichs, Statistiken, Hierarchie, Angebote-Übersicht und Prozess-Konfiguration."],
    },
    aiSeed: "Wie ist der typische Ablauf eines Wärmepumpen-Projekts vom Lead bis zur Schlussrechnung?",
  },

  dashboard: {
    title: "Dashboard – deine Tagesübersicht",
    audience: ALL_SALES,
    summary:
      "Deine Startseite. Oben stehen die Kennzahlen (Projekte gesamt, in Bearbeitung, Angebote, Gewonnen), darunter die drei " +
      "zuletzt bearbeiteten Projekte mit Kennzeichnung Wärmepumpe (Flamme) oder PV (Sonne) und die Schnellzugriffe " +
      "auf Kalender, Aufgaben, Akademie, Quiz und weitere Bereiche.",
    workflow: [
      "Morgens Kennzahlen und offene Projekte prüfen.",
      "Über „Neues Projekt“ direkt einen neuen Kunden anlegen.",
      "Ein Projekt aus „Aktuelle Projekte“ antippen, um genau dort weiterzuarbeiten.",
      "„Alle Projekte“ öffnet die vollständige Projektliste mit Suche.",
    ],
    tips: [
      "Ein Projekt ohne Angebot ist mit „Noch kein Angebot“ markiert – das ist dein nächster Arbeitsschritt.",
      "Termine und Aufgaben mit Fälligkeit findest du über die Kacheln „Kalender“ und „Aufgaben“.",
      "Die Kacheln im unteren Bereich ändern sich je nach Rolle – du siehst nur, was du nutzen darfst.",
    ],
    roleNotes: {
      sales_rep: ["Die Zahlen beziehen sich nur auf deine eigenen Projekte."],
      sales_manager: ["Die Zahlen umfassen dein ganzes Team. Über die Verwaltungs-Kacheln erreichst du Kanban, Team und Intake-Pipeline."],
      sales_director: ["Die Zahlen umfassen alle Teams unter dir. Zusätzlich erreichst du Statistiken, Hierarchie und Angebote-Übersicht."],
    },
    aiSeed: "Was zeigen die Kennzahlen auf meinem Dashboard?",
  },

  "projects.list": {
    title: "Projektliste",
    audience: ALL_SALES,
    summary:
      "Alle Projekte, auf die du Zugriff hast, sortiert nach der letzten Änderung. Die Suche findet Kundenname, Ort, PLZ, " +
      "Projekt- und Angebotsnummer. Ein Klick öffnet das Projekt mit allen Tabs.",
    workflow: [
      "Suchbegriff eingeben (z. B. Nachname oder PLZ).",
      "Optional nach Person filtern (nur Leiter und Direktoren).",
      "Projekt antippen und im gewünschten Tab weiterarbeiten.",
    ],
    tips: [
      "Projekte mit Schild-Symbol sind echte Vorgänge und vor Test-Bereinigungen geschützt.",
      "WP- und PV-Projekte erkennst du an der Projektnummer (W… / P…).",
      "Über „Neues Projekt“ legst du direkt aus der Liste einen neuen Kunden an.",
    ],
    roleNotes: {
      sales_rep: ["Du siehst nur Projekte, die dir gehören oder dir zugewiesen sind."],
      sales_manager: ["Mit „Alle Personen“ filterst du auf einen bestimmten Vertriebler deines Teams."],
      sales_director: ["Du kannst nach Vertriebsleiter (inkl. Team) oder nach einzelnen Vertrieblern filtern."],
    },
    aiSeed: "Wie finde ich schnell alle Projekte eines bestimmten Kunden?",
  },

  "projects.new": {
    title: "Neues Projekt anlegen",
    audience: ALL_SALES,
    summary:
      "Hier legst du Kunde und Projekt in einem Schritt an: Kundentyp (Privat 0 % USt. oder Gewerblich 19 %), Anrede, Name, " +
      "Kontakt, Adresse, Eigentümerstatus, Gebäudetyp, Lead-Quelle, Wunschtermin und Bemerkungen. Danach geht es im Projekt weiter.",
    fields: [
      "Kundentyp: Privatperson = 0 % USt. nach § 12 Abs. 3 UStG, Gewerblich = 19 % – steuert alle Preise im Angebot.",
      "Firmenname / USt-ID: nur bei gewerblichen Kunden nötig, erscheinen im Angebot und auf Rechnungen.",
      "Telefon und E-Mail: Voraussetzung für Angebots-Versand, Unterschrifts-Link und Tracking-Link.",
      "Eigentümerstatus: wichtig für die KfW-Förderfähigkeit.",
      "Gebäudetyp: wird automatisch in den V-Check (Abschnitt A) übernommen.",
      "Bemerkungen: per Mikrofon-Button auch diktierbar.",
    ],
    tips: [
      "Ein angefangenes Formular wird als Entwurf gespeichert und beim nächsten Öffnen wiederhergestellt.",
      "Die Adresse wird später 1:1 in Angebot, V-Check und Rechnungen übernommen – bitte vollständig erfassen.",
      "Mit „Entwurf verwerfen“ leerst du das Formular komplett.",
    ],
    aiSeed: "Welche Kundendaten brauche ich mindestens für ein Angebot mit KfW-Förderung?",
  },

  calendar: {
    title: "Kalender",
    audience: ALL_SALES,
    summary:
      "Alle Termine rund um deine Projekte (Vor-Ort-Termine, Wiedervorlagen, Installationstermine). Über „Externe Sync“ " +
      "abonnierst du den Kalender in Outlook, Google oder Apple.",
    workflow: [
      "Zeitraum wählen und Termine prüfen.",
      "Termin antippen, um zum zugehörigen Projekt zu springen.",
      "Einmalig „Externe Sync“ einrichten, damit alle Termine auch im Handy-Kalender erscheinen.",
    ],
    tips: [
      "Der abonnierte Kalender aktualisiert sich automatisch – je nach Kalender-App mit einigen Minuten Verzögerung.",
      "Termine ohne Projektbezug eignen sich für Schulungen oder Team-Meetings.",
    ],
    roleNotes: {
      sales_manager: ["Mit dem Filter „Alle Vertrieb“ siehst du die Termine deines ganzen Teams."],
      sales_director: ["Mit dem Personen-Filter siehst du die Termine aller Teams unter dir."],
    },
    aiSeed: "Wie abonniere ich meinen Novatus-Kalender in Outlook?",
  },

  tasks: {
    title: "Aufgaben",
    audience: ALL_SALES,
    summary:
      "Deine zentrale To-do-Liste aus allen Projekten, gruppiert nach Überfällig, Heute, Anstehend, Ohne Termin und Erledigt. " +
      "Viele Aufgaben entstehen automatisch, z. B. wenn im BzA-Prozess eine Frist überschritten wird.",
    workflow: [
      "Zuerst „Überfällig“ abarbeiten, dann „Heute“.",
      "Aufgabe antippen, um ins zugehörige Projekt zu springen.",
      "Erledigte Aufgaben über das Status-Symbol abhaken.",
    ],
    tips: [
      "Aufgaben, die du im Projekt-Tab „Aufgaben“ anlegst, erscheinen automatisch auch hier.",
      "Aufgaben ohne Termin regelmäßig mit einem Datum versehen, sonst gehen sie unter.",
    ],
    aiSeed: "Welche Aufgaben sollte ich nach einem versendeten Angebot anlegen?",
  },

  profile: {
    title: "Mein Profil",
    audience: "Alle angemeldeten Nutzer",
    summary:
      "Deine persönlichen Daten, Adresse, geschäftlichen Angaben (Firma, Steuernummer, USt-ID, Handelsregister) und " +
      "Bankverbindung für die Provisionsabrechnung sowie deine Anmelde-Mobilnummer.",
    tips: [
      "Die Bankverbindung wird nur für die Provisionsauszahlung verwendet.",
      "Eine geänderte Mobilnummer gilt ab dem nächsten Login – der SMS-Code geht dann an die neue Nummer.",
      "Selbstständige Vertriebler: Steuernummer bzw. USt-ID pflegen, damit Provisionsgutschriften korrekt sind.",
    ],
    aiSeed: "Welche Angaben brauche ich als selbstständiger Vertriebler für die Provisionsabrechnung?",
  },

  "messages.direct": {
    title: "Direktnachrichten",
    audience: ALL_SALES,
    summary:
      "Interner Chat zwischen zwei Kollegen, unabhängig von einem Projekt – z. B. für Rückfragen an deinen Vertriebsleiter " +
      "oder den Innendienst. Kunden sehen diese Nachrichten nie.",
    tips: [
      "Für Fragen zu einem bestimmten Projekt besser den Tab „Nachrichten“ im Projekt nutzen – dann bleibt alles am Vorgang.",
      "Neue Nachrichten werden dir als Benachrichtigung (Glocke) angezeigt.",
    ],
    aiSeed: "Wann nutze ich Direktnachrichten und wann den Projekt-Chat?",
  },

  // ------------------------------------------------------------------ Projekt-Tabs
  "project.messages": {
    title: "Projekt · Nachrichten",
    audience: ALL_SALES,
    summary:
      "Interner Chat zu genau diesem Projekt zwischen Vertrieb, Leitung, Innendienst und Back-Office. Der Kunde sieht diese " +
      "Nachrichten nie. Alles bleibt dauerhaft am Projekt nachvollziehbar.",
    tips: [
      "Rückfragen zum Projekt immer hier stellen statt per WhatsApp – so sieht jeder Beteiligte den Stand.",
      "Neue Nachrichten erscheinen bei den Beteiligten als Benachrichtigung.",
      "Für Kundenkommunikation E-Mail, Telefon oder den Tracking-Link nutzen, nicht diesen Chat.",
    ],
    aiSeed: "Wie formuliere ich eine gute Übergabe-Nachricht an das Back-Office?",
  },

  "project.customer": {
    title: "Projekt · Kunde",
    audience: ALL_SALES,
    summary:
      "Stammdaten des Kunden: Kundentyp, Anrede, Name, Firma, Kontakt, Adresse, Eigentümerstatus und Gebäudetyp. Diese Daten " +
      "erscheinen in Angebot, Auftragsbestätigung, Rechnungen und werden automatisch in den V-Check übernommen.",
    fields: [
      "Kundentyp: Privat (0 % USt.) oder Gewerblich (19 % USt.) – ändert alle Preise im Angebot.",
      "Telefon im Format +49…: nötig für SMS und den Wählen-Button.",
      "E-Mail: nötig für Angebots-Versand, Unterschrifts-Link und Tracking-Link.",
      "Adresse: wird als Objektadresse in den V-Check und ins Angebot übernommen.",
    ],
    tips: [
      "Änderungen hier werden in noch leere V-Check-Felder automatisch übernommen – bereits ausgefüllte Felder bleiben unverändert.",
      "Fehlt die E-Mail, ist der Angebots-Versand gesperrt.",
      "Sachbearbeiter (Innendienst) des Kunden wird beim Angebots-Versand für die Statistik gespeichert.",
    ],
    aiSeed: "Welche Kundendaten brauche ich für ein KfW-458-Angebot?",
  },

  "project.consumption": {
    title: "Projekt · Verbrauch & Heizlast",
    audience: ALL_SALES,
    summary:
      "Erfassung von Energieträger, Jahresverbrauch, Baujahr, Wohnfläche, Sanierungsstand und Personen im Haushalt. Daraus " +
      "berechnet die App die überschlägige Heizlast – Grundlage für die Größe der Wärmepumpe und den Paket-Vorschlag im Angebot.",
    workflow: [
      "Verbrauch der letzten Jahre vom Kunden erfragen (Jahresabrechnung).",
      "Energieträger und Menge eintragen (m³ Gas, Liter Öl, kg Pellets …) – die App rechnet in kWh um.",
      "Baujahr, beheizte Fläche, Sanierungsstand und Personenzahl ergänzen.",
      "Berechnete Heizlast prüfen – sie fließt in Angebot und V-Check ein.",
    ],
    tips: [
      "Heizlast, Jahresverbrauch, Baujahr und Fläche werden automatisch in leere V-Check-Felder übernommen.",
      "Die Berechnung ist eine Orientierung und ersetzt keine Heizlastberechnung nach DIN EN 12831.",
      "Werte über ca. 25 kW bei einem Einfamilienhaus kritisch prüfen – oft liegt ein Eingabefehler vor.",
    ],
    aiSeed: "Welche Heizlast ist bei einem teilsanierten Haus von 1985 mit 150 m² realistisch?",
  },

  "project.offer": {
    title: "Projekt · Angebot erstellen",
    audience: ALL_SALES,
    summary:
      "Hier stellst du das Wärmepumpen-Angebot zusammen: passendes Paket zur Heizlast, Zusatzpositionen, Rabatt, KfW-Förderung, " +
      "Bemerkungen und optional Novatus Care. Mit „Als Angebot“ wird daraus ein Angebot mit eigener Nummer, das im Tab „Angebote“ erscheint.",
    workflow: [
      "Paket wählen – die App schlägt es anhand der Heizlast vor.",
      "Zusatzpositionen (elektrisch, hydraulisch) und Bemerkungen ergänzen.",
      "Rabatt eintragen – Grenzen deiner Rolle werden automatisch eingehalten.",
      "KfW-Förderung prüfen.",
      "„Als Angebot“ bzw. „Als Richtpreisangebot erstellen“ wählen – erst dann entsteht ein Angebot.",
      "Im Tab „Angebote“ versenden oder direkt unterschreiben lassen.",
    ],
    fields: [
      "Festpreis-Paket: Komplettpaket inkl. Material und Montage.",
      "Einzelpreise im PDF anzeigen: blendet die Preise je Komponente im PDF ein oder aus.",
      "Bemerkungen zum Auftrag: erscheinen im PDF.",
      "Novatus Care: optionaler Wartungsvertrag.",
    ],
    tips: [
      "Nur „Speichern“ legt KEIN Angebot an – dafür immer „Als Angebot“ wählen.",
      "Bei Änderungen bleiben Angebotsnummer und Erstelldatum erhalten, Paket und Positionen werden ersetzt.",
      "Unterschriebene Angebote sind gesperrt – Änderungen nur über ein neues Angebot.",
    ],
    roleNotes: {
      sales_rep: ["Dein maximaler Rabatt ist vom Vertriebsleiter bzw. Mandanten festgelegt – darüber wird automatisch gekappt."],
      sales_manager: ["Rabattgrenzen und Marge für dein Team stellst du unter „Vertriebs-Einstellungen“ ein."],
      sales_director: ["Standard-Marge, Rabattgrenzen und Provisionen für alle Teams pflegst du in den Vertriebs-Einstellungen."],
    },
    aiSeed: "Welches Wärmepumpen-Paket passt zu 12 kW Heizlast?",
  },

  "project.offers": {
    title: "Projekt · Angebote (Liste, Versand & Unterschrift)",
    audience: ALL_SALES,
    summary:
      "Alle Angebote dieses Projekts mit Nummer, Status und PDF. Hier versendest du Angebote per E-Mail, lässt sie direkt auf dem " +
      "Gerät unterschreiben, passt Rabatt oder Provision an und siehst, was sich zwischen Versionen geändert hat.",
    workflow: [
      "Aktuelles Angebot (Kennzeichnung „AKTUELL“) auswählen.",
      "„Angebot versenden“: Empfänger, optional CC und eine persönliche Nachricht eintragen – PDF und Status-Link gehen an den Kunden.",
      "Vor Ort: „Unterschreiben“ öffnet die digitale Unterschrift für den Kunden.",
      "Nach der Unterschrift startet automatisch der weitere Ablauf (Liefer-/Leistungsvertrag, BzA-Prozess).",
    ],
    fields: [
      "Status: Entwurf, Versendet, Unterschrieben, Storniert.",
      "Änderungen: zeigt neu hinzugefügte, geänderte und entfernte Positionen gegenüber der Vorversion.",
      "Mehrverkauf: zusätzliche Positionen über dem Paketpreis.",
    ],
    tips: [
      "Ein Angebot erscheint erst hier, wenn es im Tab „Angebot“ mit „Als Angebot“ erstellt wurde.",
      "Ein Orientierungsangebot aus dem Telesales wird ebenfalls hier angezeigt und kann versendet werden.",
      "Nur echte Unterschriften zählen als „Unterschrieben“ – ein reines Versenden ändert den Status auf „Versendet“.",
    ],
    roleNotes: {
      sales_rep: ["Rabatt anpassen ist nur innerhalb deiner Rabattgrenze möglich."],
      sales_manager: ["Du kannst Rabatt und Provision für Angebote deines Teams anpassen. „Leer lassen“ behält die ursprüngliche Provision."],
      sales_director: ["Du kannst Rabatt und Provision für alle Angebote deines Bereichs anpassen."],
    },
    aiSeed: "Wie formuliere ich eine überzeugende Begleit-Nachricht zum Angebot?",
  },

  "project.tech": {
    title: "Projekt · Technik (V-Check WP / PV)",
    audience: ALL_SALES,
    summary:
      "Der V-Check ist die technische Vor-Ort-Aufnahme in 14 Abschnitten – von Objektdaten, Zugang und Bestandsanlage über " +
      "Hydraulik, Rohrleitungen und Außeneinheit bis zu Elektro und Kundenbestätigung. Eine Ampel zeigt pro Abschnitt, was fehlt " +
      "(rot) und was fertig ist (grün), oben steht der Fortschritt in Prozent.",
    workflow: [
      "Technik-Tab öffnen und oben „WP“ oder „PV“ wählen.",
      "Bereits bekannte Werte sind aus Kunde, Verbrauch und früheren Aufnahmen vorbefüllt – nur prüfen und Lücken ergänzen.",
      "Abschnitt für Abschnitt durchgehen; rote Felder sind Pflicht.",
      "Fotos mit „Jetzt fotografieren“ aufnehmen – die KI wertet sie sofort aus und zeigt einen gelben Vorschlag. Mit „Übernehmen“ oder „Verwerfen“ entscheiden.",
      "Grundriss: Plan fotografieren, hochladen oder mit „Skizze zeichnen“ selbst anlegen.",
      "Nicht vorhandene Punkte mit „Entfällt“ markieren – sie zählen dann als erledigt.",
      "Am Ende unterschreibt der Kunde; das PDF (inkl. Grundriss auf eigener Seite) kann direkt an den Installateur gesendet werden.",
    ],
    fields: [
      "Rohrleitungen: Warmwasser, Kaltwasser und Heizung jeweils mit Durchmesser (DN) und Material.",
      "Heizkreise: Anzahl, System, Vor-/Rücklauftemperatur, Mischer und Pumpe.",
      "Außeneinheit: Standort, Untergrund, Abstände zu Fenster, Grenze und Schlafraum, Kondensat.",
      "Elektro: Hausanschluss, Zählerschrank, WP-Stromkreis, Kommunikation, vorhandene PV.",
    ],
    tips: [
      "Die KI schlägt nur vor – eingetragen wird erst, wenn du „Übernehmen“ tippst.",
      "„KI-Vorschläge für den ganzen V-Check“ in der Übersicht wertet alle Fotos und Daten auf einmal aus.",
      "Offline-Eingaben und Fotos werden automatisch nachgeladen, sobald wieder Netz da ist.",
      "Arbeiten mehrere Personen gleichzeitig am V-Check, siehst du oben, wer gerade online ist.",
      "Sprachnotizen per Mikrofon werden automatisch in Text umgewandelt.",
    ],
    roleNotes: {
      sales_rep: ["Welche Felder Pflicht sind, legt deine Leitung fest. Rote Felder müssen vor der Freigabe ausgefüllt sein."],
      sales_manager: ["Unter „V-Check Pflichtfelder“ legst du fest, welche Felder für dein Team Pflicht sind."],
      sales_director: ["Unter „V-Check Pflichtfelder“ legst du fest, welche Felder im Mandanten Pflicht sind."],
    },
    aiSeed: "Welche Angaben im V-Check sind für die Wärmepumpen-Auslegung besonders kritisch?",
  },

  "project.media": {
    title: "Projekt · Medien",
    audience: ALL_SALES,
    summary:
      "Galerie aller Fotos, Skizzen und Videos zum Projekt. Fotos werden nach Kategorie abgelegt, lassen sich markieren " +
      "(z. B. Standort Außeneinheit oder Leitungsweg einzeichnen) und herunterladen.",
    workflow: [
      "„Foto (Kamera)“ oder „Aus Galerie“ wählen und eine Kategorie zuordnen.",
      "Mit „Auf diesem Foto markieren“ Standorte und Leitungswege einzeichnen.",
      "Skizzen und Grundrisse erscheinen im Bereich Skizzen, Videos im Bereich Videos.",
    ],
    tips: [
      "Die Foto-Klingeln zeigen, welche Motive noch fehlen (z. B. Typenschild, Zählerschrank).",
      "Projektfotos werden bei der KI-Gesamtauswertung im V-Check mitberücksichtigt.",
      "Markierte Fotos werden als eigene Skizze gespeichert – das Original bleibt erhalten.",
      "Lineal (Symbol unten rechts): Linie ziehen und Maß in mm eintragen. Das erste Maß ist die Referenz – danach misst die App jede weitere Linie automatisch (Wert lässt sich korrigieren).",
      "Fläche (Fünfeck-Symbol): Eckpunkte antippen, zum Schließen auf den ersten Punkt tippen oder „Fertig“ – die Fläche in m² wird eingetragen (braucht eine Lineal-Referenz).",
      "Nach jedem Maß bzw. jeder Fläche kannst du den Wert mit einem Tipp in ein V-Check-Feld übernehmen (z. B. Leitungsweg, Abstand Fenster, beheizte Fläche). Vorhandene Werte werden nur nach Rückfrage überschrieben.",
      "Zum genauen Zeichnen mit zwei Fingern oder +/− zoomen, mit der Hand verschieben, Vollbild über das Pfeil-Symbol.",
    ],
    aiSeed: "Welche Fotos brauche ich für eine vollständige Wärmepumpen-Aufnahme?",
  },

  "project.status": {
    title: "Projekt · Status & Ablauf nach Auftrag",
    audience: ALL_SALES,
    summary:
      "Zeigt, in welcher Phase das Projekt steht, vom Lead über Angebot und Unterschrift bis zu BzA, KfW-Bescheid, Anzahlung, " +
      "Installation und Schlussrechnung. Hier werden die Schritte nach der Unterschrift ausgelöst und dokumentiert.",
    workflow: [
      "Nach der Unterschrift: BzA-Unterlagen (Angebot, Liefer-/Leistungsvertrag, BzA-Excel) an den Energieberater senden.",
      "BzA-Bestätigung erfassen und an den Kunden weitergeben.",
      "Datum des KfW-Bescheids eintragen.",
      "1. Anzahlung auslösen und Zahlungseingang „Als bezahlt“ markieren.",
      "„An Back-Office übergeben“ und Installationsfirma zuweisen.",
    ],
    tips: [
      "Der Kunden-Tracking-Link zeigt dem Kunden nur die Projektphase – keine Preise und keine internen Notizen.",
      "Bei Versand an eine andere Adresse als die Kunden-Mail „Abweichende Empfänger-E-Mail“ nutzen.",
      "Jeder Schritt wird mit Datum und Benutzer in der Chronik gespeichert.",
    ],
    roleNotes: {
      sales_manager: ["Im BzA-Kanban siehst du alle Projekte deines Teams in diesem Ablauf und erkennst Verzögerungen sofort."],
      sales_director: ["Fristen und Bezeichnungen der Phasen pflegst du unter „Prozess-Konfiguration“."],
    },
    aiSeed: "Was passiert nach der Unterschrift bis zur Installation?",
  },

  "project.docs": {
    title: "Projekt · Dokumente & Kommunikation",
    audience: ALL_SALES,
    summary:
      "Übersicht aller Dokumente, die in diesem Projekt erzeugt oder versendet werden können – z. B. Angebot, Liefer-/Leistungsvertrag, " +
      "BzA-Unterlagen, Rechnungen, V-Check. Der Status zeigt, was ausstehend, erzeugt oder versendet/unterschrieben ist.",
    workflow: [
      "„Nächster Schritt“ zeigt, welches Dokument als Nächstes dran ist.",
      "Dokument erzeugen – fehlende Angaben werden per Assistent abgefragt bzw. automatisch ausgefüllt.",
      "Mit „Empfänger-Bündel“ mehrere Dokumente gemeinsam an Kunde, Energieberater oder Installateur senden.",
    ],
    tips: [
      "„Auto-Fill“ übernimmt vorhandene Projektdaten automatisch ins Dokument.",
      "Bereits versendete Dokumente sind grün markiert – doppeltes Versenden wird so vermieden.",
    ],
    aiSeed: "Welche Dokumente braucht der Energieberater für die BzA?",
  },

  "project.tasks": {
    title: "Projekt · Aufgaben",
    audience: ALL_SALES,
    summary:
      "To-dos zu genau diesem Projekt, gruppiert nach Überfällig, Heute, Anstehend, Ohne Termin und Erledigt. Sie erscheinen " +
      "zusätzlich in deiner zentralen Aufgabenliste.",
    tips: [
      "Kurze, handlungsorientierte Titel wählen („Kunde wegen Zählerschrank-Foto anrufen“).",
      "Automatisch erzeugte Aufgaben (z. B. bei überschrittenen BzA-Fristen) erscheinen ebenfalls hier.",
      "Mit dem Status-Symbol hakst du Aufgaben ab.",
    ],
    aiSeed: "Welche Aufgaben sind nach einem Vor-Ort-Termin sinnvoll?",
  },

  "project.notes": {
    title: "Projekt · Bemerkungen",
    audience: ALL_SALES,
    summary:
      "Freie Bemerkungen zum Projekt, z. B. Wünsche des Kunden, Besonderheiten vor Ort oder Absprachen. Jede Bemerkung ist mit " +
      "Autor und Datum gespeichert und kann bearbeitet oder gelöscht werden.",
    tips: [
      "Technische Details lieber direkt im V-Check erfassen – dort landen sie im Installateur-PDF.",
      "Absprachen mit dem Kunden (Preis, Termin) hier festhalten, damit Kollegen sie nachlesen können.",
    ],
    aiSeed: "Welche Absprachen sollte ich nach einem Beratungsgespräch notieren?",
  },

  "project.history": {
    title: "Projekt · Chronik",
    audience: ALL_SALES,
    summary:
      "Lückenloses Protokoll aller Änderungen und Aktionen am Projekt: wer wann was geändert, versendet oder unterschrieben hat – " +
      "inklusive automatischer Schritte des Systems.",
    tips: [
      "Einträge können nicht bearbeitet oder gelöscht werden.",
      "Bei Rückfragen des Kunden („Wann kam das Angebot?“) ist die Chronik die schnellste Quelle.",
    ],
    aiSeed: "Wie finde ich heraus, wann das Angebot versendet wurde?",
  },

  "project.pv": {
    title: "PV-Projekt · Konfigurator & Angebot",
    audience: ALL_SALES,
    summary:
      "Konfigurator für Photovoltaik: PV-Paket wählen, Positionen und Mehrwertsteuer je Position anpassen, Eigenverbrauch und Ersparnis " +
      "berechnen, Konfigurationen speichern und daraus ein PV-Angebot erzeugen und versenden. Inklusive Hinweis auf den WP-+-PV-Cross-Sell.",
    workflow: [
      "Paket wählen oder im Bearbeitungsmodus Positionen anpassen.",
      "Gesamt-Investition, Eigenverbrauch und Ersparnis pro Jahr prüfen.",
      "Konfiguration unter einem Namen speichern.",
      "„Als Angebot“ erzeugen – erst dann erscheint es im Tab „Angebote“.",
      "Angebot an den Kunden senden (optional CC an Sachbearbeiter/Buchhaltung) oder unterschreiben lassen.",
    ],
    fields: [
      "Mehrwertsteuersatz je Position: PV-Anlage für Privatkunden 0 % (§ 12 Abs. 3 UStG), Wallbox und Sonstiges 19 %.",
      "PV→WP-Bonus p.a.: zusätzliche Ersparnis, wenn der Kunde auch eine Wärmepumpe nutzt.",
    ],
    tips: [
      "Hat der Kunde Interesse an einer Wärmepumpe, die WP-+-PV-Cross-Sell-Karte nutzen.",
      "Den Signatur-Status kannst du über das Aktualisieren-Symbol neu laden.",
    ],
    aiSeed: "Wie erkläre ich dem Kunden den Vorteil von PV in Kombination mit einer Wärmepumpe?",
  },

  // ------------------------------------------------------------------ Verkauf & Pipeline
  katalog: {
    title: "Paketkatalog",
    audience: ALL_SALES,
    summary:
      "Alle Wärmepumpen-Pakete und Zusatzleistungen mit technischen Daten, Material, Montage, Beratung & Begleitung sowie dem " +
      "Gesamtpreis brutto – ideal, um dem Kunden im Gespräch Pakete zu zeigen.",
    tips: [
      "Die Abschnitte (Leistung, Material, Montage, Beratung) lassen sich einzeln aufklappen.",
      "Die angezeigten Preise enthalten deine Marge (Kundensicht).",
    ],
    roleNotes: {
      sales_rep: ["Du siehst die Endkundenpreise inkl. Marge."],
      sales_manager: ["Du siehst zusätzlich die Abgabepreise an den Vertrieb (Manager-Ansicht)."],
      sales_director: ["Du siehst zusätzlich die Abgabepreise an den Vertrieb (Manager-Ansicht)."],
    },
    aiSeed: "Worin unterscheiden sich die Wärmepumpen-Pakete?",
  },

  "kanban.bza": {
    title: "BzA-Kanban – Ablauf nach der Unterschrift",
    audience: `${REP} · ${LEAD} · ${DIR} · Back-Office · ${ADM}`,
    summary:
      "Alle unterschriebenen Projekte im Ablauf nach Auftrag: BzA beantragt → BzA erhalten → BzA an Kunde und Auftrag bestätigt → " +
      "KfW-Bestätigung erhalten → 1. Anzahlung → Projektplanung. Der farbige Rand zeigt die Frist: grün im Plan, gelb knapp, rot überfällig.",
    workflow: [
      "Oben die Zähler für überfällige und warnende Projekte prüfen.",
      "Rote Karten zuerst bearbeiten – Karte antippen öffnet das Projekt; der Ablauf steht dort im Tab „Status“.",
      "„Aufgaben für Überfällige anlegen“ erzeugt für überfällige Projekte automatisch Aufgaben an.",
    ],
    tips: [
      "Hier erscheinen nur wirklich unterschriebene Angebote.",
      "Die Fristen je Spalte stellt der Vertriebsdirektor bzw. Admin in der Prozess-Konfiguration ein.",
    ],
    roleNotes: {
      sales_rep: ["Du siehst nur deine eigenen Projekte."],
      sales_manager: ["Du siehst alle Projekte deines Teams und kannst gezielt nachfassen."],
      sales_director: ["Du siehst alle Projekte deines Bereichs."],
    },
    aiSeed: "Wie beschleunige ich den BzA-Prozess beim Energieberater?",
  },

  "manager.kanban": {
    title: "Kanban – Vertriebs-Pipeline",
    audience: LEADS_UP,
    summary:
      "Alle Projekte deines Teams als Karten in Spalten nach Phase. So siehst du auf einen Blick, wo Projekte hängen. " +
      "Unsignierte Angebote stehen nie in den Spalten nach der Unterschrift.",
    workflow: [
      "Spalten von links nach rechts prüfen: Wo stauen sich Projekte?",
      "Karte antippen, um das Projekt zu öffnen.",
      "Die Spalte „An Back-Office“ zeigt (nur lesend), welche Projekte bereits übergeben sind – die Übergabe selbst erfolgt im Projekt-Tab „Status“.",
    ],
    tips: [
      "Die Phase ergibt sich aus echten Ereignissen (Angebot erstellt, versendet, unterschrieben) – nicht aus manuellen Schiebe-Aktionen.",
      "Für den Ablauf nach der Unterschrift das BzA-Kanban nutzen.",
    ],
    roleNotes: {
      sales_manager: ["Du siehst die Projekte aller Vertriebler deines Teams."],
    },
    aiSeed: "Woran erkenne ich im Kanban, dass ein Projekt feststeckt?",
  },

  "manager.team": {
    title: "Team-Auswertung",
    audience: LEADS_UP,
    summary:
      "Kennzahlen je Vertriebler deines Teams: Projekte, Angebote, Abschlüsse, Umsatz und Abschlussquote. Klick auf einen Namen " +
      "öffnet die Detailansicht des Mitarbeiters mit seinen Projekten.",
    tips: [
      "Vergleiche Abschlussquoten im selben Zeitraum, sonst sind die Zahlen nicht vergleichbar.",
      "Niedrige Quote bei vielen Angeboten deutet auf Nachfass-Bedarf hin.",
      "Demo- und Testprojekte zählen nicht mit.",
    ],
    aiSeed: "Welche Kennzahl ist für die Vertriebssteuerung am wichtigsten?",
  },

  "pipeline.intake": {
    title: "Intake-Pipeline – Innendienst → Außendienst",
    audience: LEADS_UP,
    summary:
      "Alle vom Innendienst (Telesales) angelegten Projekte, die noch an einen Außendienst-Vertriebler übergeben werden müssen. " +
      "Die Ampel zeigt, ob das Projekt übergabereif ist (grün) oder noch Angaben fehlen (gelb – Tooltip zeigt die Lücken).",
    workflow: [
      "Grüne Projekte zuerst übergeben.",
      "„An Vertrieb übergeben“ → Vertriebler unter „Zuweisen an“ wählen.",
      "Optional eine Übergabe-Notiz für den Vertriebler schreiben.",
    ],
    tips: [
      "Der zugewiesene Vertriebler sieht das Projekt danach sofort in seiner Projektliste.",
      "Bei gelben Projekten zuerst mit dem Innendienst klären, welche Angaben fehlen.",
    ],
    aiSeed: "Welche Informationen braucht der Außendienst bei der Übergabe vom Innendienst?",
  },

  "manager.sales-settings": {
    title: "Vertriebs-Einstellungen – Marge, Provision, Rabatt",
    audience: LEADS_UP,
    summary:
      "Hier legst du fest, mit welcher Marge, welchem Provisionssatz und welchen Rabattgrenzen dein Team arbeitet, und welche " +
      "Abläufe automatisch laufen (z. B. Tagesübersicht per E-Mail). Werte vererben sich: Mandant → Direktor → Leiter → Vertriebler.",
    fields: [
      "Marge: Anteil am Endkundenpreis. Beispiel: Einkauf 12.000 € bei 15 % Marge → Endkundenpreis 12.000 / (1 − 0,15) = 14.117,65 €.",
      "Provision & Rabatt: Provisionssatz und maximaler Rabatt in % bzw. €.",
      "Deine Übersteuerung: eigener Wert für dein Team; leer lassen = Standard erben.",
      "Automatisierung: wann das System eigenständig Aktionen ausführt (z. B. bei Montagebeginn oder Inbetriebnahme).",
      "Tagesübersicht – Empfänger: wer die tägliche Zusammenfassung per E-Mail bekommt.",
    ],
    tips: [
      "Änderungen wirken nur auf neue Angebote – bestehende Angebote behalten ihre Preise.",
      "Alle Änderungen werden protokolliert.",
    ],
    roleNotes: {
      sales_manager: ["Deine Werte gelten für dein Team und dürfen die Grenzen deines Direktors nicht überschreiten."],
      sales_director: ["Deine Werte sind der Standard für alle Teams unter dir."],
    },
    aiSeed: "Wie wirkt sich eine Margenänderung auf den Endkundenpreis aus?",
  },

  "admin.health": {
    title: "Pipeline-Health – Frühwarnsystem",
    audience: LEADS_UP,
    summary:
      "Zeigt Projekte, die Aufmerksamkeit brauchen: offene Anzahlungsrechnungen, verkaufte Projekte ohne vollständigen " +
      "Tech-Check und überfällige Übergaben an den Installateur. Ist alles in Ordnung, erscheint „Alle Pipelines laufen sauber“.",
    tips: [
      "Jeder Eintrag führt direkt zum betroffenen Projekt.",
      "Mit „Aktualisieren“ lädst du den neuesten Stand.",
      "Verkauft ohne Tech-Check bedeutet: Der Installateur hat noch keine verlässlichen Daten – zuerst V-Check vervollständigen.",
    ],
    aiSeed: "Wie senke ich die Zahl der verkauften Projekte ohne vollständigen Tech-Check?",
  },

  "admin.offers": {
    title: "Angebote – Übersicht",
    audience: `${DIR} · Telesales-Direktor · ${ADM}`,
    summary:
      "Alle Angebote des Mandanten mit Suche nach Nummer, Name oder Firma, Statusfilter und Zeitraum. Oben stehen Trefferzahl, " +
      "versendete/unterschriebene Angebote und das Volumen brutto.",
    tips: [
      "Filter kombinieren, z. B. „Unterschrieben“ + Monatszeitraum für den Monatsumsatz.",
      "Ein Klick auf eine Zeile öffnet das Projekt.",
      "Auf dem Handy die Tabelle seitlich wischen, um alle Spalten zu sehen.",
    ],
    aiSeed: "Wie ermittle ich das Angebotsvolumen eines Monats?",
  },

  // ------------------------------------------------------------------ Team & Organisation
  "admin.users": {
    title: "Benutzerverwaltung",
    audience: `${LEAD} · ${DIR} · Telesales-Direktor · ${ADM}`,
    summary:
      "Benutzer anlegen, per Link einladen, Rollen vergeben, sperren und der Hierarchie zuordnen (Direktor → Leiter → Vertriebler). " +
      "Admins erreichen hier zusätzlich die Live-Übersicht und den DSGVO-Report.",
    workflow: [
      "„Einladen per Link“ → Rolle wählen → Link an den neuen Mitarbeiter senden.",
      "Neuen Benutzer seinem Vertriebsleiter zuordnen.",
      "Bei Austritt Benutzer sperren statt löschen – die Projekte bleiben erhalten.",
    ],
    tips: [
      "Rollenänderungen wirken nach dem nächsten Login.",
      "Die Tabellen-Ansicht zeigt zusätzlich Projekte, Abschlüsse und Umsatz je Benutzer.",
    ],
    roleNotes: {
      sales_manager: ["Du verwaltest nur Benutzer in deinem eigenen Team."],
      sales_director: ["Du verwaltest alle Leiter und Vertriebler unter dir."],
    },
    aiSeed: "Was passiert mit den Projekten, wenn ich einen Vertriebler sperre?",
  },

  "admin.users-table": {
    title: "Benutzer-Tabelle",
    audience: `${LEAD} · ${DIR} · Telesales-Direktor · ${ADM}`,
    summary:
      "Tabellarische Übersicht aller Benutzer mit Name, Rolle, Status, Kontakt, Firma, USt-ID sowie Kennzahlen zu Projekten, " +
      "unterschriebenen Angeboten, Umsatz und offenen Vorgängen.",
    tips: [
      "Spaltenköpfe anklicken, um zu sortieren (z. B. nach Umsatz).",
      "Fehlende USt-ID bei selbstständigen Vertrieblern hier schnell erkennen und nachfordern.",
    ],
    aiSeed: "Wie erkenne ich, welcher Vertriebler die meisten offenen Vorgänge hat?",
  },

  "admin.hierarchy": {
    title: "Vertriebs-Hierarchie",
    audience: `${DIR} · Telesales-Direktor · ${ADM}`,
    summary:
      "Organigramm von Admin über Vertriebsdirektoren und Vertriebsleiter bis zu den Vertrieblern und Telesales-Agents – " +
      "inklusive nicht zugeordneter Benutzer.",
    tips: [
      "Nicht zugeordnete Benutzer sehen keine Team-Projekte und erscheinen in keiner Team-Auswertung – zeitnah zuordnen.",
      "Zuordnungen änderst du in der Benutzerverwaltung.",
    ],
    aiSeed: "Wie baue ich eine sinnvolle Team-Struktur für 20 Vertriebler auf?",
  },

  "admin.branches": {
    title: "Niederlassungen",
    audience: DIR_UP,
    summary:
      "Niederlassungen unterhalb des Mandanten mit eigenem Vertriebsaufschlag, eigenen Rabattgrenzen, Ko-Branding im Angebots-PDF " +
      "und Aufteilung des Mehrverkaufs zwischen Projektierung und Betreuung.",
    fields: [
      "Marge-Typ / Marge-Wert: Aufschlag in Prozent oder als fester Euro-Betrag.",
      "Max. Rabatt % / €: Obergrenze für Rabatte in dieser Niederlassung.",
      "Mehrverkauf-Split: Anteil des Vertriebs am Mehrverkauf in %.",
      "Ko-Branding: eigene PDF-Kopfzeile und Akzentfarbe (Hex) im Angebot.",
    ],
    tips: ["Änderungen wirken nur auf neu erstellte Angebote."],
    aiSeed: "Wie richte ich eine neue Niederlassung mit eigenem Branding ein?",
  },

  "admin.feedback": {
    title: "Feedback der Tester",
    audience: `${LEAD} · ${ADM}`,
    summary:
      "Alle über den Feedback-Button gemeldeten Fehler und Wünsche mit Beschreibung und Screenshot. Der Status zeigt, ob eine " +
      "Meldung offen, in Bearbeitung oder erledigt ist.",
    tips: [
      "Den Status ändern, sobald du eine Meldung gesichtet hast – so weiß das Team, was bearbeitet wird.",
      "Der Screenshot zeigt genau die Ansicht, in der das Problem auftrat.",
    ],
    aiSeed: "Wie priorisiere ich Feedback aus meinem Team?",
  },

  "admin.sachbearbeiter-stats": {
    title: "Sachbearbeiter-Statistik",
    audience: LEADS_UP,
    summary:
      "Zeigt pro Sachbearbeiter (Innendienst) die Anzahl versendeter WP- und PV-Angebote im gewählten Monat. Grundlage ist der " +
      "beim Versand gespeicherte Sachbearbeiter des Kunden.",
    tips: [
      "Monat und Jahr oben wählen.",
      "Fehlt ein Sachbearbeiter, wurde er beim Kunden nicht eingetragen – im Kunden-Tab ergänzen.",
    ],
    aiSeed: "Wie werte ich die Leistung des Innendienstes fair aus?",
  },

  "admin.email-logs": {
    title: "E-Mail-Protokoll",
    audience: LEADS_UP,
    summary:
      "Alle versendeten E-Mails des Mandanten – Auftragsbestätigungen, Anzahlungs- und Schlussrechnungen, BzA-Pakete, KfW-Bescheide, " +
      "Orientierungsangebote und Angebots-Versand – mit Empfänger, Zeitpunkt und Erfolg.",
    workflow: [
      "Ereignis-Typ und Status (erfolgreich / fehlgeschlagen) wählen.",
      "Zeitraum eingrenzen oder nach Kunde bzw. Empfänger suchen.",
      "Über den Projekt-Link direkt zum Vorgang springen.",
    ],
    tips: [
      "Fehlgeschlagene Mails zuerst prüfen – meist ist die Empfängeradresse falsch.",
      "Ideal bei Rückfragen wie „Ich habe die Auftragsbestätigung nie bekommen“.",
    ],
    aiSeed: "Was mache ich, wenn eine Auftragsbestätigung nicht zugestellt wurde?",
  },

  "admin.signature-audit": {
    title: "Signatur-Audit",
    audience: `${LEAD} · ${DIR} · Telesales-Direktor · ${ADM}`,
    summary:
      "Alle unterschriebenen Angebote mit dem Status ihrer Dokumente (signiertes Angebot und Liefer-/Leistungsvertrag). " +
      "Fehlt ein Dokument, lässt es sich mit einem Klick neu erzeugen.",
    tips: [
      "„Unvollständig“ bedeutet: Die Unterschrift liegt vor, aber mindestens ein PDF fehlt.",
      "Ein gesendeter BzA-Reminder wird ebenfalls angezeigt.",
    ],
    aiSeed: "Warum kann ein signiertes Angebot ohne Liefer-/Leistungsvertrag sein?",
  },

  "admin.manual-signatures": {
    title: "Manuelle Signaturen (DSGVO-Audit)",
    audience: DIR_UP,
    summary:
      "Alle Unterschriften, die nicht digital in der App, sondern auf Papier oder über einen anderen Kanal erfasst wurden – mit " +
      "Kanal, Unterzeichner, Datum der Unterschrift und wer sie wann erfasst hat.",
    tips: [
      "Nach Kanal und Zeitraum filtern.",
      "Klick auf einen Eintrag öffnet das Projekt.",
    ],
    aiSeed: "Was muss ich bei einer Papier-Unterschrift dokumentieren?",
  },

  "admin.invoice-tools": {
    title: "Rechnungs-Werkzeuge",
    audience: LEADS_UP,
    summary:
      "Erzeugt alle gespeicherten Rechnungs-PDFs mit der aktuellen Vorlage neu – z. B. nach einer Layout-Änderung. Rechnungsnummer " +
      "und Datum bleiben dabei unverändert.",
    workflow: [
      "Zuerst prüfen lassen, welche Rechnungen betroffen sind (Liste mit Rechnungsnummer und Größe).",
      "„Alle Rechnungs-PDFs neu rendern“ bestätigen.",
      "Den Bericht prüfen.",
    ],
    tips: ["Bereits versendete Rechnungen werden nicht erneut verschickt."],
    aiSeed: "Wann sollte ich Rechnungs-PDFs neu erzeugen?",
  },

  "admin.document-templates": {
    title: "Dokumenten-Verwaltung",
    audience: LEADS_UP,
    summary:
      "Liste aller Dokument-Vorlagen der App (Angebote, Verträge, Rechnungen, V-Check …). Du legst fest, wie ein Dokument heißt, zu " +
      "welcher Kategorie es gehört, in welchen Projektphasen es angeboten wird, wer es bekommt und ob es aktiv ist.",
    fields: [
      "Anzeigename / Beschreibung: so erscheint das Dokument im Projekt-Tab „Dokumente“.",
      "Pipeline-Stages: in welchen Phasen das Dokument angeboten wird.",
      "Empfänger: Kunde, Installateur, Energieberater …",
      "Aktiv: nur aktive Vorlagen erscheinen im Dokumente-Tab.",
    ],
    tips: ["Das Layout fest eingebauter Vorlagen ist nicht änderbar – nur Name, Zuordnung und Sichtbarkeit."],
    aiSeed: "Wie steuere ich, in welcher Projektphase ein Dokument angeboten wird?",
  },

  "admin.text-blocks": {
    title: "Textbausteine",
    audience: LEADS_UP,
    summary:
      "Wiederverwendbare Texte für Dokumente und E-Mails (Grußformeln, Zahlungshinweise, Signatur-Fußzeilen …) mit Formatierung und " +
      "Platzhaltern wie ${kunde.name}. Eine Vorschau zeigt den Text mit Beispieldaten.",
    workflow: [
      "Neuen Baustein anlegen, Kategorie wählen.",
      "Text formatieren und Platzhalter einfügen.",
      "Vorschau bzw. „Als PDF testen“ prüfen und speichern.",
    ],
    tips: ["Platzhalter werden beim Erzeugen automatisch durch die Projektdaten ersetzt."],
    aiSeed: "Wie formuliere ich einen professionellen Zahlungshinweis?",
  },

  "admin.vcheck-rules": {
    title: "V-Check Pflichtfelder",
    audience: LEADS_UP,
    summary:
      "Hier legst du fest, welche Felder und Foto-Blöcke im V-Check Wärmepumpe Pflicht sind. Pflichtfelder erscheinen im V-Check rot, " +
      "bis sie ausgefüllt oder als „Entfällt“ markiert sind, und bestimmen den Fortschritt in Prozent.",
    tips: [
      "Nur Felder zur Pflicht machen, die der Installateur wirklich braucht – zu viele Pflichtfelder bremsen den Vertrieb.",
      "Änderungen gelten sofort für alle offenen V-Checks.",
    ],
    aiSeed: "Welche V-Check-Felder sollten Pflicht sein?",
  },

  "admin.project-stages": {
    title: "Prozess-Konfiguration (BzA-Ablauf & Energieberater)",
    audience: DIR_UP,
    summary:
      "Bezeichnung, Beschreibung und Frist (SLA) für jede Phase des BzA-Ablaufs. Die Fristen bestimmen die Farben im BzA-Kanban " +
      "und wann automatische Aufgaben entstehen. Zusätzlich pflegst du hier die Energieberater.",
    tips: [
      "Realistische Fristen wählen – zu kurze Fristen erzeugen ständig rote Karten.",
      "Neue Energieberater mit E-Mail anlegen, damit BzA-Pakete direkt versendet werden können.",
    ],
    aiSeed: "Welche Fristen sind im BzA-Prozess realistisch?",
  },

  "admin.produkte": {
    title: "Produkte (WP & PV)",
    audience: DIR_UP,
    summary:
      "Zentrale Pflege aller Produkte in Tabs: PV-Katalog, PV-Pakete, WP-Katalog, WP-Komponenten und WP-Pakete. Preise und Pakete " +
      "wirken auf neue Angebote.",
    tips: [
      "Produkte deaktivieren statt löschen – bestehende Angebote bleiben so unverändert.",
      "Nach Preisänderungen ein Test-Angebot erstellen und prüfen.",
    ],
    aiSeed: "Wie lege ich ein neues Wärmepumpen-Paket an?",
  },

  "admin.quiz-results": {
    title: "Quiz-Ergebnisse",
    audience: DIR_UP,
    summary: "Ergebnisse des Schulungsquiz aller Mitarbeiter mit Punktzahl, Datum und Zertifikat als PDF.",
    tips: [
      "Mitarbeiter mit niedriger Punktzahl gezielt nachschulen.",
      "Das Zertifikat lässt sich direkt herunterladen.",
    ],
    aiSeed: "Wie nutze ich Quiz-Ergebnisse für die Schulungsplanung?",
  },

  "admin.empfehlungsbonus": {
    title: "Empfehlungsbonus-Auswertung",
    audience: DIR_UP,
    summary:
      "Monatliche Übersicht der Empfehlungsboni: beansprucht, freigegeben, offen und abgelehnt, dazu die Top-Empfehlungsgeber und " +
      "die letzten Anspruchs-Ereignisse.",
    tips: ["Zeitraum (3, 6, 12 oder 24 Monate) oben wählen."],
    aiSeed: "Wie steigere ich die Zahl der Kundenempfehlungen?",
  },

  "admin.auction-stats": {
    title: "Auktion-Statistik",
    audience: DIR_UP,
    summary:
      "Kennzahlen zum Installateur-Marktplatz: Auktionen, Gebote, Gebotsquote, vergebene Aufträge, Auftragsvolumen, Ablehnungsquote " +
      "und Pönalen sowie die aktivsten Installateure.",
    tips: ["Eine hohe Ablehnungsquote deutet auf zu niedrige Startpreise oder ungünstige Regionen hin."],
    aiSeed: "Wie bewerte ich die Leistung der Installateure im Marktplatz?",
  },

  "admin.sms-stats": {
    title: "SMS-Versandstatistik",
    audience: DIR_UP,
    summary: "Versand von Login-Codes und Mahn-SMS: Anzahl, Zustellrate, Annahmequote, Fehler und geschätzte Kosten sowie Auffälligkeiten im Verlauf.",
    tips: ["Häufen sich Fehler, Rufnummern-Formate (+49…) im Kunden- bzw. Benutzerprofil prüfen."],
    aiSeed: "Warum werden SMS manchmal nicht zugestellt?",
  },

  "admin.briefing-ab": {
    title: "PV-Briefing A/B-Test",
    audience: DIR_UP,
    summary:
      "Vergleich zweier PV-Briefing-Varianten (zahlen-fokussiert vs. story-fokussiert): Versand, Soft-Conversion (Status geht weiter) " +
      "und Hard-Conversion (Auftrag unterschrieben).",
    tips: ["Erst ab einigen Dutzend Versänden pro Variante sind die Unterschiede aussagekräftig."],
    aiSeed: "Welche Briefing-Variante überzeugt Kunden eher?",
  },

  "admin.inbound-leads": {
    title: "Orientierungsangebote",
    audience: `${LEAD} · ${DIR} · Telesales-Direktor · ${ADM}`,
    summary:
      "Alle vom Telesales bzw. über Online-Anfragen erzeugten Orientierungsangebote mit Status. Von hier aus wird das Projekt an den " +
      "Vertrieb weitergegeben und zum verbindlichen Angebot ausgearbeitet.",
    tips: [
      "Orientierungsangebote sind unverbindlich – das verbindliche Angebot entsteht erst nach dem V-Check.",
      "Lange offene Orientierungsangebote zeitnah nachfassen.",
    ],
    aiSeed: "Wie mache ich aus einem Orientierungsangebot einen Auftrag?",
  },

  "admin.activity": {
    title: "Nutzer-Aktivität – Arbeitszeiten",
    audience: "Nur Admin",
    summary:
      "Zeigt, welcher Nutzer wann und wie lange in der App gearbeitet hat: Arbeitszeit je Nutzer für Heute, Gestern, 7 oder 30 Tage und " +
      "pro Tag die Arbeitsblöcke mit Beginn, Ende und Dauer auf einer Zeitleiste von 0 bis 24 Uhr.",
    workflow: [
      "Zeitraum oben wählen.",
      "Nutzer antippen – die Tagesansicht mit allen Arbeitsblöcken klappt auf.",
      "Mit „CSV“ alle Blöcke (Nutzer, Datum, Beginn, Ende, Dauer) für Excel exportieren.",
    ],
    fields: [
      "Arbeitsblock: zusammenhängende Arbeitszeit; nach mehr als 15 Minuten ohne Bedienung beginnt ein neuer Block.",
      "Dauer: Zeit von Beginn bis Ende eines Blocks; „Aktive Minuten“ im Export zählt nur Minuten mit Bedienung.",
    ],
    tips: [
      "Nur echte Bedienung zählt – eine offen liegende, unbenutzte App erzeugt keine Arbeitszeit.",
      "Die Daten werden 30 Tage gespeichert und danach automatisch gelöscht.",
      "Vor einer Nutzung zur Leistungs- oder Verhaltenskontrolle Datenschutz bzw. Betriebsrat einbeziehen.",
    ],
    aiSeed: "Wie werte ich Arbeitszeiten im Vertrieb datenschutzkonform aus?",
  },

  "admin.system_health": {
    title: "System-Health – Selbstdiagnose",
    audience: DIR_UP,
    summary:
      "Prüft die wichtigsten Grundeinstellungen des Mandanten auf einen Blick: Wärmepumpen-Katalog, Komponenten und Pakete befüllt, " +
      "Firmenanschrift plausibel, E-Mail-Absender und Versand eingerichtet, Referenzpreise gepflegt, Zahlungsanbieter im Test- oder Live-Modus.",
    tips: [
      "Rote Punkte zuerst beheben – wo möglich gibt es einen 1-Klick-Fix, der gefahrlos mehrfach ausgeführt werden kann.",
      "Nach dem Anlegen eines neuen Mandanten hier einmal alles prüfen, bevor Angebote verschickt werden.",
    ],
    aiSeed: "Was muss für einen neuen Mandanten eingerichtet sein, bevor Angebote verschickt werden?",
  },

  "register.pending": {
    title: "Freigabe ausstehend",
    audience: "Neu registrierte Nutzer",
    summary:
      "Deine Registrierung ist eingegangen. Ein Admin bzw. deine Vertriebsleitung prüft und schaltet dein Konto frei. Danach kannst " +
      "du dich mit deiner Mobilnummer anmelden.",
    tips: ["Bei längerer Wartezeit deine Vertriebsleitung direkt ansprechen."],
    aiSeed: "Wie lange dauert die Freigabe nach der Registrierung?",
  },
};

// „Wer sieht das?“ für bestehende Einträge aus helpContent.js
export const AUDIENCE_PATCH = {
  "admin.handbook": "Alle angemeldeten Nutzer (Rollen-Handbuch)",
  feedback: "Alle angemeldeten Nutzer",
  abo: "Alle angemeldeten Nutzer mit eigenem Abo",
  "select-plan": "Alle angemeldeten Nutzer mit eigenem Abo",
  quiz: "Alle angemeldeten Nutzer",
  "lead-auctions": "Alle angemeldeten Nutzer (Marktplatz)",
  "admin.installer-applications": DIR_UP,
  "admin.penalties": DIR_UP,
  "admin.tenants": DIR_UP,
  "admin.system_health": DIR_UP,
};

export function roleNotesFor(content, role) {
  const notes = content?.roleNotes || {};
  return notes[role] || (role === "branch_manager" ? notes.sales_manager : null) || [];
}
